<template>
  <section class="performance" data-testid="wealth-performance">
    <PerformanceHeader
      :is-loading="isHeaderLoading"
      :has-error="hasError"
      :period-filters="periodFilters"
      :benchmark-filters="benchmarkFilters"
      @on-benchmark-filter-change="handleBenchmarkFilterChange"
      @on-period-filter-change="handlePeriodFilterChange"
    />
    <PerformanceChart
      :axis-x="chartAxisX"
      :axis-y="chartAxisY"
      :benchmark-filters="benchmarkFilters"
      :updated-at="updatedAt"
      :is-loading="isChartLoading"
      :has-error="hasError"
      :height="height"
      @on-animation-state-change="handleOnAnimationStateChange"
      @on-retry="fetchWealthChart"
    />
    <div class="performance__buttons">
      <slot name="actions" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';

import { PerformanceChart } from '@/modules/wealth/components/PerformanceChart';
import { PerformanceHeader } from './components/PerformanceHeader';

import {
  getWealthChart,
  PortfolioChartAxisX,
  PortfolioChartAxisY,
  PortfolioChartFilter,
  WealthChartParams,
  PortfolioChartSummary
} from '@/modules/wealth/services/portfolio';

import { getFilterOptions } from '@/modules/wealth/views/IndexPortfolio/helpers';

import { calculateChartHeight } from '@/modules/wealth/helpers/calculate-height-chart';

const isLoading = ref(true);
const hasError = ref(false);
const periodFilters = ref<PortfolioChartFilter[]>([]);
const benchmarkFilters = ref<PortfolioChartFilter[]>([]);
const portfolioChartSummary = ref<PortfolioChartSummary | null>(null);
const chartAxisX = ref<PortfolioChartAxisX[]>([]);
const chartAxisY = ref<PortfolioChartAxisY[]>([]);
const updatedAt = ref<string | null>(null);
const isChartAnimationInProgress = ref(false);

const portfolioParams = computed<WealthChartParams>(() => {
  return {
    periodFilter: getActiveFilter(periodFilters.value),
    benchmark: getActiveFilter(benchmarkFilters.value)
  };
});

const isHeaderLoading = computed(() => {
  return isLoading.value || isChartAnimationInProgress.value;
});

const isChartLoading = computed(() => {
  return (
    isLoading.value &&
    chartAxisX.value.length === 0 &&
    chartAxisY.value.length === 0
  );
});

const height = computed(() => {
  return calculateChartHeight(window.innerHeight);
});

async function fetchWealthChart() {
  isLoading.value = true;
  hasError.value = false;

  try {
    const chart = await getWealthChart(portfolioParams.value);

    periodFilters.value = getFilterOptions(chart.periodFilters);
    benchmarkFilters.value = getFilterOptions(chart.benchmarks);
    portfolioChartSummary.value = chart.summary;
    chartAxisX.value = chart.axisX;
    chartAxisY.value = chart.axisY;
    updatedAt.value = chart.updatedAt.formatted;
  } catch (error) {
    hasError.value = true;
    isChartAnimationInProgress.value = false;
  } finally {
    isLoading.value = false;
  }
}

function handleOnAnimationStateChange(isAnimationInProgress: boolean) {
  isChartAnimationInProgress.value = isAnimationInProgress;
}

function getActiveFilter(filters: PortfolioChartFilter[] | null) {
  if (!filters) {
    return '';
  }

  return filters.find(filter => filter.active)?.key;
}

async function handleBenchmarkFilterChange(value: string) {
  benchmarkFilters.value = benchmarkFilters.value.map(filter => ({
    ...filter,
    active: filter.value === value
  }));

  await fetchWealthChart();
}

async function handlePeriodFilterChange(value: string) {
  periodFilters.value = periodFilters.value.map(filter => ({
    ...filter,
    active: filter.value === value
  }));

  await fetchWealthChart();
}

onMounted(async () => {
  await fetchWealthChart();
});
</script>

<style lang="less" scoped>
.performance {
  margin-bottom: @size-spacing-x400;

  &__buttons {
    margin-top: @size-spacing-x400;
  }
}
</style>
